import React from 'react';

import ProcessingIndicator from '../../Common/ProcessingIndicator';
import ActionButton from '../../Common/ActionButton';
import NoticeBlock from '../../Common/NoticeBlock';
import MessageList from '../../Common/MessageList';
import Field from "../../Fields/Field";
import InputTextField from "../../Fields/InputTextField";
import InputPayorPanel from '../../Fields/InputPayorPanel';
import { getRedirectUrl } from '../../../Utils/envUrl';

import {
  queryGetSubscription,
  queryUpdatePayor,
  queryUpdateSubscription
} from '../../../Queries';

import Subscription from '../../../Entities/Subscription';
import Payor from '../../../Entities/Payor';
import featureFlags from '../../../featureFlags';
import {
  validateSubscriptionName,
  sanitizeRemoveSpaceFromStart,
  sanitizeRemoveSpaceFromEnd,
  sanitizeRemoveDoubleSpaces
} from '../../../Validators/Subscription';

const payorQueryFields = [
  'id',
  'readable_id',
  'type',
  'status',
  'contact_notes',
  'billing_firstname',
  'billing_lastname',
  'billing_phone',
  'billing_work_phone',
  'billing_email',
  {
    'billing_emails': [
      'id',
      'payor_id',
      'email'
    ]
  },
  'billing_street',
  'billing_city',
  'billing_state',
  'billing_zip',
  'info_name',
  'info_attn',
  'info_street',
  'info_city',
  'info_state',
  'info_zip',
  'cognito_sub'
];
const billingQueryFields = [
  'name',
  'validation_only',
];

const subscriptionQueryFields = featureFlags.SUBSCRIPTION_NAME ? [
  { 'payor': payorQueryFields },
  'subscription_name',
  'status',
  'subscription_invoice_notes',
  'account_id',
  'tax_exempt',
  'numberOfParkingSpaces',
  { 'billing_profile': billingQueryFields }
] : [
  { 'payor': payorQueryFields },
  'status',
  'subscription_invoice_notes',
  'account_id',
  'tax_exempt',
  'numberOfParkingSpaces',
  { 'billing_profile': billingQueryFields }
]

const subscriptionNameQueryFields = [
  'subscription_name',
  'subscription_invoice_notes',
]

const subscriptionStatusField = [
  'status',
];

async function fetchSubscription(subscriptionId) {
  const response = await queryGetSubscription(
    { subscription_id: subscriptionId },
    subscriptionQueryFields,
  );

  if (response.errors) {
    throw response.errors.map(({ message }) => message);
  }

  const data = response.data.getSubscription
  const subscription = Subscription.createFromQueryValues(data);
  const payor = subscription.fields.payor.value;

  return {
    payor,
    subscriptionName: data.subscription_name,
    subscriptionInvoiceNotes: data.subscription_invoice_notes,
    subscription: data,
    accountId: 'AC-' + data.account_id,
    subscriptionStatus: data.status,
    validationOnly: data.validation_only,
    billingProfile: data.billing_profile,
    taxExemptStatus: data.tax_exempt,
    numberOfParkingSpaces: data.numberOfParkingSpaces,
  };
}


class SubscriptionDetailPayorPanel extends React.Component {
  state = {
    panelInitializationComplete: false,
    panelInitializationErrors: [],
    editMode: false,
    reassignEmailMode: false,
    payor: null,
    payorPassword: null,
    savePayorTaskInProgress: false,
    savePayorTaskSuccess: null,
    savePayorTaskErrors: [],
    subscriptionName: '',
    subscriptionInvoiceNotes: '',
    subscriptionStatus: '',
    taxExemptStatus: false,
    accountId: '',
    validationOnly: null,
    numberOfParkingSpaces: null,
  };

  fieldConfiguration = {};
  facilityId;

  constructor(props) {
    super(props);

    const urlSearchParams = new URLSearchParams(window.location.search);
    this.facilityId = urlSearchParams.get('facility');
  }

  async componentDidMount() {
    const initializationTasks = [];

    const {
      subscriptionId = null,
      setCompanyName
    } = this.props;

    initializationTasks.push(async () => {
      const values = await Payor.getFieldAllowedValues();
      this.fieldConfiguration.payor = values;
    });

    initializationTasks.push(async () => {
      const values = await Subscription.getFieldAllowedValues(['status', 'validation_only', 'tax_exempt']);
      this.fieldConfiguration.subscription = values;
    });

    initializationTasks.push(async () => {
      await this.refreshSubscription(subscriptionId);
    });

    try {
      await Promise.all(initializationTasks.map(task => task()));

      this.setState({ panelInitializationComplete: true });

      const companyName = this.state.payor.fields.info_name.value;
      if (companyName) {
        setCompanyName(companyName);
      }
    } catch (errors) {
      this.setState({
        panelInitializationComplete: true,
        panelInitializationErrors: errors
      });
    }
  }

  refreshSubscription = async (subscriptionId) => {
    const subscription = await fetchSubscription(subscriptionId);
    this.setState(subscription);
  }


  savePayor(payor) {
    return new Promise((resolve, reject) => {
      const variables = {
        payor: payor.getQueryFieldValues()
      };
      const { subscriptionName, subscriptionInvoiceNotes } = this.state;
      const sanitizedName = sanitizeRemoveSpaceFromEnd(subscriptionName);

      const subscriptionVariables = {
        subscription_id: this.props.subscriptionId,
        subscription: {
          subscription_name: sanitizedName,
          subscription_invoice_notes: subscriptionInvoiceNotes,
          tax_exempt: this.state.taxExemptStatus,
          numberOfParkingSpaces: this.state.numberOfParkingSpaces,
        }
      }
      try {
        validateSubscriptionName(sanitizedName)
      } catch (error) {
        return this.setState({
          savePayorTaskErrors: error,
          savePayorTaskSuccess: false,
          savePayorTaskInProgress: false,
        });
      }

      queryUpdateSubscription(subscriptionVariables, subscriptionNameQueryFields).then(response => {
        if (response.errors) {
          reject(response.errors.map(error => error.message));
          return;
        }
      }, error => {
        reject([error]);
      });

      queryUpdatePayor(variables, payorQueryFields).then(response => {
        if (response.errors) {
          reject(response.errors.map(error => error.message));
          return;
        }

        const payor = Payor.createFromQueryValues(response.data.updatePayor);

        resolve(payor);
      }, error => {
        reject([error]);
      });
    });
  }

  updateAccountStatus = () => {
    const subscriptionVariables = {
      subscription_id: this.props.subscriptionId,
      subscription: {
        status: this.state.subscriptionStatus === 'ACTIVE' ? 'TERMINATED' : 'ACTIVE'
      }
    }
    return new Promise((resolve, reject) => {
      queryUpdateSubscription(subscriptionVariables, subscriptionStatusField).then(response => {
        if (response.errors) {
          reject(response.errors.map(error => error.message));
        } else {
          this.setState({
            subscriptionStatus: response.data.updateSubscription.status
          });
        }
        resolve(response.data.updateSubscription);
      }, error => {
        reject([error]);
      });
    });
  }


  storePayor = payor => {
    this.setState({
      payor,
      savePayorTaskSuccess: null
    });
  }

  onSubscriptionInvoiceNotesChange = subscriptionInvoiceNotes => {
    this.setState({ subscriptionInvoiceNotes });
  }

  onSubscriptionExemptStatusChange = taxExemptStatus => {
    this.setState({ taxExemptStatus: taxExemptStatus === "true" ? true : false });
  }

  onSubscriptionNumParkingSpacesChange = numberOfParkingSpaces => {
    this.setState({ numberOfParkingSpaces });
  }


  onEditPayorClick = () => {
    this.setState({
      editMode: true
    });
  }

  onSavePayorClick = () => {
    this.setState({
      savePayorTaskInProgress: true,
      savePayorTaskSuccess: null,
      savePayorTaskErrors: []
    }, () => {
      this.savePayor(this.state.payor).then(payor => {
        this.setState({
          payor,
          savePayorTaskInProgress: false,
          savePayorTaskSuccess: true,
          editMode: false
        });
      }, errors => {
        this.setState({
          savePayorTaskInProgress: false,
          savePayorTaskSuccess: false,
          savePayorTaskErrors: errors
        });
      });
    });
  }

  setReassignMode = (value) => {
    this.setState({
      reassignEmailMode: value
    });
  }


  renderPanelInitializationBlock() {
    const {
      panelInitializationComplete,
      panelInitializationErrors
    } = this.state;

    return (
      <React.Fragment>
        {(!panelInitializationComplete) &&
          <div className="parkchirp--page--user-message-container">
            <ProcessingIndicator userMessage="Initializing, please wait..." />
          </div>
        }
        {(panelInitializationComplete && panelInitializationErrors.length > 0) &&
          <div className="parkchirp--page--user-message-container">
            <div className="parkchirp--page--user-message">
              <p>The following errors occurred while initializing the Payor panel:</p>
              <ul>
                {panelInitializationErrors.map(error =>
                  <li key={performance.now()}>{error}</li>
                )}
              </ul>
            </div>
          </div>
        }
      </React.Fragment>
    );
  }


  renderPayorForm() {
    const {
      payor,
      payorPassword,
      editMode,
      reassignEmailMode,
      billingProfile,
      subscriptionInvoiceNotes,
      numberOfParkingSpaces,
    } = this.state;

    return (
      <InputPayorPanel
        payor={payor}
        payorPassword={payorPassword}
        onSubscriptionExemptStatusChange={this.onSubscriptionExemptStatusChange}
        subscriptionInvoiceNotes={subscriptionInvoiceNotes}
        fieldConfiguration={this.fieldConfiguration}
        onPayorInputChange={this.storePayor}
        refreshSubscription={this.refreshSubscription}
        onSubscriptionInvoiceNotesChange={this.onSubscriptionInvoiceNotesChange}
        onSubscriptionNumParkingSpacesChange={this.onSubscriptionNumParkingSpacesChange}
        editMode={editMode}
        subscriptionStatus={this.state.subscriptionStatus}
        validationOnly={billingProfile.validation_only}
        newMode={false}
        billingProfile={billingProfile}
        reassignEmailMode={reassignEmailMode}
        taxExemptStatus={this.state.taxExemptStatus}
        setReassignMode={this.setReassignMode}
        subscriptionId={this.props.subscriptionId}
        numberOfParkingSpaces={numberOfParkingSpaces}
      />
    );
  }


  renderSidebar() {
    const {
      editMode,
      reassignEmailMode,
      savePayorTaskInProgress,
      savePayorTaskSuccess,
      savePayorTaskErrors,
      subscriptionStatus,
    } = this.state;

    const url = getRedirectUrl(window.origin, 'subscriptions/tax-exempt-certificates');
    
    const facilityParam = this.facilityId ? `&facility=${this.facilityId}&subscriptionId=${this.props.subscriptionId}` : ''
    
    return (
      <React.Fragment>
        <div className="parkchirp--sidebar-block"
          style={{
            display: 'flex',
            justifyContent: 'center',
            paddingTop: '20px'
          }}>
          {(!editMode && !reassignEmailMode) &&
            <>
              {featureFlags.PAYOR_SEARCH &&
                <>
                  {this.state.billingProfile.validation_only &&
                    <button type="button"
                      className="button button-primary"
                      onClick={this.updateAccountStatus}
                      style={{ marginLeft: '8px', marginRight: '8px' }}
                    >
                      {subscriptionStatus === 'ACTIVE' ? 'Deactivate' : 'Activate'} Account
                    </button>
                  }
                  <a href={`?page=parkchirp_credit${facilityParam}`}
                    id='parkchirp--credit-button'
                    className="button button-primary"
                  >
                    Credit
                  </a>
                  <a href={`?page=parkchirp_invoice${facilityParam}`}
                    id='parkchirp--manual-invoice-button'
                    className="button button-primary"
                    style={{ marginLeft: '8px', marginRight: '8px' }}
                  >
                    Manual Invoice
                  </a>
                  <a href={`${url}&accountId=${this.props.accountId}`}
                    id='parkchirp--manual-invoice-button2'
                    className="button button-primary"
                    style={{ marginLeft: '8px', marginRight: '8px' }}
                    target='_blank' rel="noreferrer"
                  >
                    Upload Tax Exempt Certificate
                  </a>
                </>
              }
              <button type="button"
                className="button button-primary"
                onClick={this.onEditPayorClick}>
                Edit
              </button>
            </>
          }
          {(editMode) &&
            <ActionButton label="Save"
              inProgressState={savePayorTaskInProgress}
              onClick={this.onSavePayorClick}
            />
          }
        </div>
        {(savePayorTaskSuccess === true) &&
          <NoticeBlock type="message"
            containerClasses={['parkchirp--sidebar-block']}>
            <p>Payor was successfully saved.</p>
          </NoticeBlock>
        }
        {(savePayorTaskSuccess === false && savePayorTaskErrors.length > 0) &&
          <NoticeBlock type="error"
            containerClasses={['parkchirp--sidebar-block']}>
            <MessageList heading="The following errors occurred while attempting to save the Payor:"
              messages={savePayorTaskErrors}
            />
          </NoticeBlock>
        }
      </React.Fragment>
    );
  }


  renderPanelMainContent() {
    const { reassignEmailMode, subscriptionName, editMode, accountId } = this.state;

    return (
      <div className="parkchirp--grid">
        <div className="parkchirp--grid-column parkchirp--grid-column-size--main-content">
          <div style={{ display: "flex", flexDirection: "row" }}>
            {featureFlags.SUBSCRIPTION_NAME &&
              <div style={{ width: "50%" }}>
                <Field label="Subscription Name"
                  id="parkchirp--payor--password"
                  layout="columns">
                  <InputTextField inputId="parkchirp--payor--password"
                    value={subscriptionName}
                    editMode={editMode}
                    onChange={(e) => {
                      let value = sanitizeRemoveSpaceFromStart(e);
                      value = sanitizeRemoveDoubleSpaces(e);
                      this.setState({ subscriptionName: value })
                    }}
                  />
                </Field>
              </div>
            }
            {featureFlags.ACCOUNT_ID &&
              <div style={{ width: "50%" }}>
                <div className="parkchirp--field-container parkchirp--field-container--columns">
                  <label className="parkchirp--field-label parkchirp--field-label--sm">Account ID</label>
                  <div className="parkchirp--field-value">
                    <div className="parkchirp--input-text-field">{accountId}</div>
                  </div>
                </div>
              </div>
            }
          </div>
          {this.renderPayorForm()}
        </div>
        {!reassignEmailMode &&
          <div className="parkchirp--grid-column parkchirp--grid-column-size--sidebar">
            {this.renderSidebar()}
          </div>
        }
      </div>
    );
  }

  render() {
    const {
      panelInitializationComplete,
      panelInitializationErrors
    } = this.state;

    return (
      <React.Fragment>
        {(!panelInitializationComplete || panelInitializationErrors.length > 0) &&
          this.renderPanelInitializationBlock()
        }
        {(panelInitializationComplete && panelInitializationErrors.length === 0) &&
          this.renderPanelMainContent()
        }
      </React.Fragment>
    );
  }
}

export default SubscriptionDetailPayorPanel
